import { Theme } from '@chronosphereio/chrono-ui';
import { TitleComponentOption } from 'echarts';
import { merge } from 'lodash';
import { ChartsTheme, EChartsTheme } from '../model';

const DEFAULT_TEXT_COLOR = '#222';

// avoid component override type errors since only palette and typography are used
type MuiTheme = Omit<Theme, 'components'>;

export const DEFAULT_NO_DATA_MESSAGE = 'No data.';

// TODO: refactor approach if charts theme no longer needs to be overridden
export function generateChartsTheme(
  muiTheme: MuiTheme,
  echartsTheme?: EChartsTheme,
  /**
   * The id of the container that will have the chart tooltip appended to it.
   * By default, chart tooltip uses the body of the top-level document object.
   */
  tooltipPortalContainerId?: string
): ChartsTheme {
  const primaryTextColor = muiTheme.featureFlags.panwRebrandStubYear
    ? muiTheme.palette.text.default
    : (muiTheme.palette.text?.primary ?? DEFAULT_TEXT_COLOR);

  const borderDefault = muiTheme.featureFlags.panwRebrandStubYear
    ? muiTheme.palette.border.default
    : muiTheme.palette.grey[300];
  const borderBold = muiTheme.featureFlags.panwRebrandStubYear
    ? muiTheme.palette.border.bold
    : muiTheme.palette.grey[600];
  const borderBoldLighterLegacy = muiTheme.featureFlags.panwRebrandStubYear
    ? muiTheme.palette.border.bold
    : muiTheme.palette.grey[500];

  // These two axis types have different default options so using this object
  // to ensure they stay consistent
  const valueOrLogAxisTheme = {
    show: true,
    axisLabel: {
      color: primaryTextColor,
      margin: 10,
    },
    axisLine: {
      show: false,
    },
    splitLine: {
      show: true,
      lineStyle: {
        width: 0.5,
        // this one is intentionally different, borderDefault is too subtle for the chart split lines
        color: muiTheme.featureFlags.panwRebrandStubYear ? muiTheme.palette.border.bold : muiTheme.palette.grey[300],
        opacity: 0.6,
      },
    },
  };

  const muiConvertedTheme: EChartsTheme = {
    title: {
      show: false,
    },
    textStyle: {
      color: primaryTextColor,
      fontFamily: muiTheme.typography.fontFamily,
      fontSize: 12,
    },
    grid: {
      top: 5,
      right: 20,
      bottom: 0,
      left: 20,
      containLabel: true,
    },
    // https://www.figma.com/file/bRoUgi8hJLBdNphmMnFkr7/%F0%9F%9A%A7-Chrono-UI?type=design&node-id=3829-72816&mode=design&t=KL7ED1NmnjAyfKlv-0
    color: [
      '#68C7FF', // sky
      '#005BCE', // cobalt
      '#FF9292', // salmon
      '#9C597E', // mauve
      '#02B4C1', // teal
      '#816AB5', // lilac
      '#98003C',
    ],
    categoryAxis: {
      show: true,
      axisLabel: {
        show: true,
        color: primaryTextColor,
        margin: 10,
      },
      axisTick: {
        show: false,
        length: 6,
        lineStyle: {
          color: borderBold,
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: borderBold,
        },
      },
      splitLine: {
        show: false,
        lineStyle: {
          width: 0.5,
          color: borderDefault,
          opacity: 0.4,
        },
      },
      splitArea: {
        show: false,
        areaStyle: {
          color: [borderDefault],
        },
      },
    },
    timeAxis: {
      show: true,
      axisLabel: {
        show: true,
        color: primaryTextColor,
        margin: 10,
      },
      axisTick: {
        show: false,
        length: 6,
        lineStyle: {
          color: borderBold,
        },
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: borderBold,
        },
      },
      axisPointer: {
        z: 1, // Prevents crosshair label from showing underneath xAxis timestamps
        label: {
          margin: 0,
          padding: 3,
          backgroundColor: muiTheme.palette.primary.main,
          borderRadius: 0,
          color: muiTheme.palette.text.inversePrimary,
        },
      },
      splitLine: {
        show: false,
        lineStyle: {
          width: 0.5,
          color: borderDefault,
          opacity: 0.4,
        },
      },
      splitArea: {
        show: false,
        areaStyle: {
          color: [borderDefault],
        },
      },
    },
    valueAxis: { ...valueOrLogAxisTheme },
    logAxis: { ...valueOrLogAxisTheme },
    legend: {
      orient: 'horizontal',
      textStyle: {
        color: primaryTextColor,
      },
      pageTextStyle: {
        color: muiTheme.featureFlags.panwRebrandStubYear ? undefined : muiTheme.palette.grey[600],
      },
      pageIconColor: muiTheme?.palette?.action?.active,
      pageIconInactiveColor: muiTheme?.palette?.action?.disabled,
    },
    toolbox: {
      show: true,
      top: 10,
      right: 10,
      iconStyle: {
        borderColor: primaryTextColor,
      },
    },
    tooltip: {
      backgroundColor: muiTheme.featureFlags.panwRebrandStubYear
        ? muiTheme.palette.background.tooltip
        : muiTheme.palette.designSystem?.grey[800],
      borderColor: muiTheme.featureFlags.panwRebrandStubYear ? undefined : muiTheme.palette.designSystem?.grey[800],
      borderWidth: muiTheme.featureFlags.panwRebrandStubYear ? 0 : undefined,
      textStyle: {
        color: muiTheme.featureFlags.panwRebrandStubYear ? muiTheme.palette.text.default : '#fff',
        fontSize: 11,
      },
      axisPointer: {
        shadowStyle: {
          // v5 shadow style for consistency when doing v5 upgrade. In the future, we may want to adjust this to align
          // with colors from the spotlight theme.
          // https://github.com/apache/echarts/blob/513918064ac2a0866433d434dc969220f12b9c1a/theme/v5.js#L294C13-L296C14
          color: 'rgba(210,219,238,0.2)',
        },
      },
    },
    axisPointer: {
      lineStyle: {
        color: borderBoldLighterLegacy,
      },
    },
    markLine: {
      symbol: 'none',
      symbolSize: 0,
      itemStyle: {
        color: borderBoldLighterLegacy,
      },
      lineStyle: {
        type: 'dashed',
        width: 1,
      },
    },
    line: {
      showSymbol: false,
      symbol: 'circle',
      symbolSize: 4,
      smooth: false,
      lineStyle: {
        width: 1,
      },
      emphasis: {
        lineStyle: {
          width: 1.5,
        },
      },
    },
    bar: {
      barMaxWidth: 150,
      itemStyle: {
        borderWidth: 0,
        borderRadius: 0,
        borderColor: borderDefault,
      },
      label: {
        show: false,
        color: primaryTextColor,
      },
    },
    gauge: {
      detail: {
        fontSize: 18,
        fontWeight: 600,
        valueAnimation: false,
      },
      splitLine: {
        distance: 0,
        length: 4,
        lineStyle: {
          width: 1,
        },
      },
      splitNumber: 12,
    },
  };

  return {
    theme: muiTheme,
    tooltipPortalContainerId,
    echartsTheme: merge(muiConvertedTheme, echartsTheme),
    noDataOption: {
      title: {
        show: true,
        textStyle: {
          color: primaryTextColor,
          fontSize: 16,
          fontWeight: 400,
        },
        text: DEFAULT_NO_DATA_MESSAGE,
        left: 'center',
        top: 'center',
      },
      xAxis: {
        show: false,
      },
      yAxis: {
        show: false,
      },
    },
    sparkline: {
      width: 2,
      color: '#1976d2',
    },
    container: {
      padding: {
        x: parseInt(muiTheme.spacing(1), 10),
        y: parseInt(muiTheme.spacing(1), 10),
      },
    },
    thresholds: {
      defaultColor: muiTheme.palette.primary.main,
      palette: [
        muiTheme.palette.threshold.warning,
        muiTheme.featureFlags.panwRebrandStubYear ? muiTheme.palette.status.error.main : muiTheme.palette.error.main,
      ],
      labelBackgroundColor: muiTheme.palette.background.overlay,
      labelColor: muiTheme.featureFlags.panwRebrandStubYear ? muiTheme.palette.text.default : 'white',
    },
    yAxis: {
      label: {
        chartPadding: parseInt(muiTheme.spacing(1), 10),
      },
    },
    stripedDecal: {
      dashArrayX: [1, 0],
      dashArrayY: [4, 3],
      rotation: -Math.PI / 4,
      color: 'white',
    },
    confidenceBandColor: muiTheme.featureFlags.panwRebrandStubYear
      ? muiTheme.palette.status.default.lowContrast
      : muiTheme.palette.grey[200],
  };
}

/**
 * Get the no data message from the charts theme.
 */
export function getNoDataMessage(chartsTheme: ChartsTheme) {
  return (chartsTheme.noDataOption.title as TitleComponentOption).text;
}
